import React from "react";

import { StatusDetail } from "../../../common/components";
import { StatusEntry } from "../../../common/components/StatusDetail";
import { ClaimStatus, BesuClaimState, ClaimHistory } from "../../../types/claim";
import { buildUniqueStatusHistory } from "../../../utils/common";

import { ClaimStatusBadge } from "./ClaimStatusBadge";

type ClaimStatusDetailProps = {
    claimHistory: ClaimHistory;
    latestState: BesuClaimState;
    noCA?: boolean;
};


const ClaimStatusDetail = ({
    claimHistory,
    latestState,
    noCA,
}: ClaimStatusDetailProps): JSX.Element => {
    return (
        <StatusDetail
            className="mt-1 fs-7 fw-normal float-end"
            statusEntries={toStatusEntries(claimHistory, noCA)}
        >
            <ClaimStatusBadge claimState={latestState} />
        </StatusDetail>
    );
};

const toStatusEntries = (claimHistory: ClaimHistory, noCA?: boolean): StatusEntry[] => {
    const historyItems = claimHistory.statusHistory;
    const entries = buildUniqueStatusHistory(historyItems)
        // ClaimStatus.PARTIAL_CA_APPROVED should only display in CTA
        .filter((it) => it.status !== ClaimStatus.PARTIAL_CA_APPROVED)
        .map((history): StatusEntry => {
            switch (
                history.status as Exclude<
                    ClaimStatus,
                    ClaimStatus.PARTIAL_CA_APPROVED
                >
            ) {
                case ClaimStatus.ALL_CA_APPROVED: {
                    if (noCA) {
                        return {
                            role: "Service Provider",
                            date: history.timestamp,
                            status: "Created",
                            badgeType: "success",
                        };
                    } else {
                        return {
                            role: "Claim Approvers",
                            date: history.timestamp,
                            status: "Approved",
                            badgeType: "success",
                        };
                    }
                }
                case ClaimStatus.CA_REJECTED:
                    return {
                        role: "Claim Approvers",
                        date: history.timestamp,
                        status: "Rejected",
                        badgeType: "danger",
                    };
                case ClaimStatus.APPROVED:
                    return {
                        role: "Project Officer",
                        date: history.timestamp,
                        status: "Approved",
                        badgeType: "success",
                    };
                case ClaimStatus.PO_REJECTED:
                    return {
                        role: "Project Officer",
                        date: history.timestamp,
                        status: "Rejected",
                        badgeType: "danger",
                    };
                case ClaimStatus.SUBMITTED:
                    return {
                        role: "Service Provider",
                        date: history.timestamp,
                        status: "Created",
                        badgeType: "success",
                    };
                case ClaimStatus.RESUBMITTED:
                    return {
                        role: "Service Provider",
                        date: history.timestamp,
                        status: "Submitted",
                        badgeType: "success",
                    };
                case ClaimStatus.DISCARDED:
                    return {
                        role: "Service Provider",
                        date: history.timestamp,
                        status: "Discarded",
                        badgeType: "discarded",
                    };
            }
            throw new Error(`Unsupported claim status: ${history.status}`);
        });

    let firstEntry: StatusEntry | null = null;
    switch (historyItems[0]?.status) {
        case ClaimStatus.SUBMITTED:
        case ClaimStatus.RESUBMITTED:
        case ClaimStatus.PARTIAL_CA_APPROVED: {
            if (noCA) {
                firstEntry = {
                    role: "Project Officer",
                    date: null,
                    status: "CTA",
                    badgeType: null,
                };
                break;
            } else {
                firstEntry = {
                    role: "Claim Approvers",
                    date: null,
                    status: "CTA",
                    badgeType: null,
                };
                break;
            }
        }
        case ClaimStatus.ALL_CA_APPROVED:
            firstEntry = {
                role: "Project Officer",
                date: null,
                status: "CTA",
                badgeType: null,
            };
            break;
        case ClaimStatus.PO_REJECTED:
        case ClaimStatus.CA_REJECTED:
            firstEntry = {
                role: "Service Provider",
                date: null,
                status: "CTA",
                badgeType: null,
            };
            break;
    }

    return firstEntry ? [firstEntry].concat(entries) : entries;
};

export default ClaimStatusDetail;
